import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import moment from 'moment-timezone';
import { EnrollmentExportRow, exportEnrollmentsThunk } from '@spike/memberships-action';
import { useNonInitialEffect } from '@versiondos/hooks';
import ExportCSV from 'components/UI/ExportCSV/ExportCSV';
import { RootState } from 'store';
import { useApiClientWrapper, useMarketplace, useTimeZone } from 'hooks';
import { EnrollmentUsageSelection } from './EnrollmentUsageFilters';

interface EnrollmentsExportCSVProps {
    status?: string;
    usageFilters: EnrollmentUsageSelection;
}

const csvHeaders = [
    'Client Name',
    'Client Email',
    'Client Phone',
    'Pets',
    'Membership',
    'Type',
    'Status',
    'Start Date',
    'End Date',
    'Next Billing Date',
    'Remaining Uses'
];

// ExportCSV writes without enclosing characters and with ';' as separator, so these would break the columns.
const cleanCell = (value: string | null | undefined) => (value ?? '').replace(/[;\r\n]+/g, ' ').trim();

const formatRemainingUses = (uses: EnrollmentExportRow['remainingUses']) => {
    if (uses === 'unlimited') return 'Unlimited';
    return uses == null ? '' : String(uses);
};

const EnrollmentsExportCSV: React.FC<EnrollmentsExportCSVProps> = ({ status, usageFilters }) => {
    const dispatch = useDispatch<ThunkDispatch<RootState, null, AnyAction>>();
    const apiClientWrapper = useApiClientWrapper();
    const marketplace = useMarketplace();
    const timeZone = useTimeZone();
    const csvRef = useRef<any>(null);
    const [loading, setLoading] = useState(false);
    const [records, setRecords] = useState<string[][]>([]);

    useNonInitialEffect(() => {
        if (records.length > 0) {
            csvRef.current?.export();
        }
    }, [records]);

    const formatDate = (date: string | null) => (date ? moment.tz(date, timeZone).format('YYYY-MM-DD') : '');

    const exportHandler = async () => {
        setLoading(true);
        try {
            const rows: EnrollmentExportRow[] = await dispatch(
                exportEnrollmentsThunk(apiClientWrapper, status, usageFilters)
            );
            setRecords(
                rows.map(row =>
                    [
                        row.clientName,
                        row.email,
                        row.phone,
                        row.pets,
                        row.membershipName,
                        row.enrollmentType === 'package' ? 'Package' : 'Membership',
                        row.status,
                        formatDate(row.startDate),
                        formatDate(row.endDate),
                        formatDate(row.nextBillingDate),
                        formatRemainingUses(row.remainingUses)
                    ].map(cleanCell)
                )
            );
        } catch (error) {
            // the thunk already shows the error alert
        } finally {
            setLoading(false);
        }
    };

    const fileName = `Memberships_Export_${marketplace.basics.businessName.replace(/\s+/g, '')}_${moment().format(
        'YYYYMMDD_HHmm'
    )}.csv`;

    return (
        <ExportCSV
            ref={csvRef}
            headers={csvHeaders}
            records={records}
            filename={fileName}
            exporting={loading}
            onClick={exportHandler}
        />
    );
};

export default EnrollmentsExportCSV;
